import { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"

// ============================================================================
// ROLE TYPES - Define all roles in one place
// ============================================================================
export const ROLE_TYPES = {
  SUPER_ADMIN: "superAdmin",
  ADMIN: "admin",
  MANAGING_DIRECTOR: "managingDirector",
  GENERAL_MANAGER: "generalManager",
  ENQUIRY_MANAGER: "enquiryManager",
  ENQUIRY_ANALYST: "enquiryAnalyst",
  SALES_EXECUTIVE: "salesExecutive",
  SALES_MANAGER: "salesManager",
  BRANCH_MANAGER: "branchManager",
  PURCHASE_MANAGER: "purchaseManager",
  INSPECTION_USER: "inspectionUser",
  BUSINESS_DEVELOPMENT_OFFICER: "businessDevelopmentOfficer",
  TEAM_MEMBER: "teamMember",
  MEMBER: "member",
  CLIENT: "client",
}

// ============================================================================
// ROLE LABELS - Display names for roles
// ============================================================================
export const ROLE_LABELS = {
  [ROLE_TYPES.SUPER_ADMIN]: "Super Admin",
  [ROLE_TYPES.ADMIN]: "Admin",
  [ROLE_TYPES.MANAGING_DIRECTOR]: "Managing Director",
  [ROLE_TYPES.GENERAL_MANAGER]: "General Manager",
  [ROLE_TYPES.ENQUIRY_MANAGER]: "Enquiry Manager",
  [ROLE_TYPES.ENQUIRY_ANALYST]: "Enquiry Analyst",
  [ROLE_TYPES.SALES_EXECUTIVE]: "Sales Executive",
  [ROLE_TYPES.SALES_MANAGER]: "Sales Manager",
  [ROLE_TYPES.BRANCH_MANAGER]: "Branch Manager",
  [ROLE_TYPES.PURCHASE_MANAGER]: "Purchase Manager",
  [ROLE_TYPES.INSPECTION_USER]: "Inspection User",
  [ROLE_TYPES.BUSINESS_DEVELOPMENT_OFFICER]: "BDE",
  [ROLE_TYPES.TEAM_MEMBER]: "Team Member",
  [ROLE_TYPES.MEMBER]: "Member",
  [ROLE_TYPES.CLIENT]: "Client",
}

// ============================================================================
// ROLE HIERARCHY - Define role levels for permission checks
// ============================================================================
export const ROLE_HIERARCHY = {
  [ROLE_TYPES.SUPER_ADMIN]: 10,
  [ROLE_TYPES.MANAGING_DIRECTOR]: 9,
  [ROLE_TYPES.GENERAL_MANAGER]: 8,
  [ROLE_TYPES.ADMIN]: 7,
  [ROLE_TYPES.ENQUIRY_MANAGER]: 6,
  [ROLE_TYPES.SALES_MANAGER]: 6,
  [ROLE_TYPES.PURCHASE_MANAGER]: 6,
  [ROLE_TYPES.BRANCH_MANAGER]: 5,
  [ROLE_TYPES.ENQUIRY_ANALYST]: 4,
  [ROLE_TYPES.SALES_EXECUTIVE]: 4,
  [ROLE_TYPES.INSPECTION_USER]: 3,
  [ROLE_TYPES.BUSINESS_DEVELOPMENT_OFFICER]: 3,
  [ROLE_TYPES.TEAM_MEMBER]: 3,
  [ROLE_TYPES.MEMBER]: 2,
  [ROLE_TYPES.CLIENT]: 1,
}

// ============================================================================
// ROLE OPTIONS FOR DROPDOWNS
// ============================================================================
export const ROLE_OPTIONS_PRIMARY = [
  { label: ROLE_LABELS[ROLE_TYPES.ADMIN], value: ROLE_TYPES.ADMIN },
  { label: ROLE_LABELS[ROLE_TYPES.CLIENT], value: ROLE_TYPES.CLIENT },
  { label: ROLE_LABELS[ROLE_TYPES.MEMBER], value: ROLE_TYPES.MEMBER },
]

export const ROLE_OPTIONS_MEMBER = [
  {
    label: ROLE_LABELS[ROLE_TYPES.MANAGING_DIRECTOR],
    value: ROLE_TYPES.MANAGING_DIRECTOR,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.GENERAL_MANAGER],
    value: ROLE_TYPES.GENERAL_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.ENQUIRY_MANAGER],
    value: ROLE_TYPES.ENQUIRY_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.ENQUIRY_ANALYST],
    value: ROLE_TYPES.ENQUIRY_ANALYST,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.SALES_MANAGER],
    value: ROLE_TYPES.SALES_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.SALES_EXECUTIVE],
    value: ROLE_TYPES.SALES_EXECUTIVE,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.BRANCH_MANAGER],
    value: ROLE_TYPES.BRANCH_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.PURCHASE_MANAGER],
    value: ROLE_TYPES.PURCHASE_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.INSPECTION_USER],
    value: ROLE_TYPES.INSPECTION_USER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.BUSINESS_DEVELOPMENT_OFFICER],
    value: ROLE_TYPES.BUSINESS_DEVELOPMENT_OFFICER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.TEAM_MEMBER],
    value: ROLE_TYPES.TEAM_MEMBER,
  },
]

export const ROLE_OPTIONS_HQ = [
  {
    label: ROLE_LABELS[ROLE_TYPES.MANAGING_DIRECTOR],
    value: ROLE_TYPES.MANAGING_DIRECTOR,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.GENERAL_MANAGER],
    value: ROLE_TYPES.GENERAL_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.ENQUIRY_ANALYST],
    value: ROLE_TYPES.ENQUIRY_ANALYST,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.ENQUIRY_MANAGER],
    value: ROLE_TYPES.ENQUIRY_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.BRANCH_MANAGER],
    value: ROLE_TYPES.BRANCH_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.PURCHASE_MANAGER],
    value: ROLE_TYPES.PURCHASE_MANAGER,
  },
  {
    label: ROLE_LABELS[ROLE_TYPES.BUSINESS_DEVELOPMENT_OFFICER],
    value: ROLE_TYPES.BUSINESS_DEVELOPMENT_OFFICER,
  },
]

// ============================================================================
// SUBORDINATE ROLES - Define which roles can manage which
// ============================================================================
export const ROLE_SUBORDINATES = {
  [ROLE_TYPES.ENQUIRY_MANAGER]: [ROLE_TYPES.ENQUIRY_ANALYST],
  [ROLE_TYPES.SALES_MANAGER]: [ROLE_TYPES.SALES_EXECUTIVE],
  [ROLE_TYPES.ADMIN]: [ROLE_TYPES.MEMBER],
  [ROLE_TYPES.SUPER_ADMIN]: Object.values(ROLE_TYPES),
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get role options based on type
 * @param {string} type - 'primary' | 'member' | 'hq' | null (returns all primary)
 * @returns {Array} Array of role options
 */
export const getRoleOptions = (type = null) => {
  switch (type) {
    case "member":
      return ROLE_OPTIONS_MEMBER
    case "hq":
      return ROLE_OPTIONS_HQ
    default:
      return ROLE_OPTIONS_PRIMARY
  }
}

/**
 * Get the display label for a role
 * @param {string} role - Role value
 * @returns {string} Display label
 */
export const getRoleLabel = role => {
  return ROLE_LABELS[role] || role
}

// ============================================================================
// MAIN HOOK - useRole
// ============================================================================

/**
 * Custom hook for role-based access control
 * @returns {Object} Role utilities and checks
 */
export const useRole = () => {
  const { user } = useSelector(state => ({
    user: state.Login.user,
  }))

  // Get current role from Redux or session storage
  const currentRole = useMemo(
    () => user?.role || localStorage.getItem("role"),
    [user?.role]
  )

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  const isRole = useCallback(role => currentRole === role, [currentRole])

  /**
   * Check if user has any of the provided roles
   * @param {Array<string>} roles - Array of roles to check
   * @returns {boolean}
   */
  const isAnyRole = useCallback(
    roles => Array.isArray(roles) && roles.includes(currentRole),
    [currentRole]
  )

  /**
   * Check if user has a role equal to or higher in hierarchy
   * @param {string} role - Role to compare
   * @returns {boolean}
   */
  const hasRoleOrHigher = useCallback(
    role => {
      const currentHierarchy = ROLE_HIERARCHY[currentRole] || 0
      const requiredHierarchy = ROLE_HIERARCHY[role] || 0
      return currentHierarchy >= requiredHierarchy
    },
    [currentRole]
  )

  /**
   * Check if user has a role lower in hierarchy
   * @param {string} role - Role to compare
   * @returns {boolean}
   */
  const hasRoleLower = useCallback(
    role => {
      const currentHierarchy = ROLE_HIERARCHY[currentRole] || 0
      const requiredHierarchy = ROLE_HIERARCHY[role] || 0
      return currentHierarchy < requiredHierarchy
    },
    [currentRole]
  )

  /**
   * Get subordinate roles that current role can manage
   * @returns {Array<string>} Array of subordinate roles
   */
  const getSubordinateRoles = useCallback(
    () => ROLE_SUBORDINATES[currentRole] || [],
    [currentRole]
  )

  /**
   * Get the hierarchy level of current role
   * @returns {number} Hierarchy level (0 if role not found)
   */
  const getRoleHierarchy = useCallback(
    () => ROLE_HIERARCHY[currentRole] || 0,
    [currentRole]
  )

  /**
   * Get display label for current role
   * @returns {string} Display label
   */
  const getRoleDisplayLabel = useCallback(
    () => getRoleLabel(currentRole),
    [currentRole]
  )

  // role groups

  // ========================================================================
  // SEMANTIC ROLE GROUPS - Grouped permissions for common use cases
  // ========================================================================
  /**
   * Check if user has admin privileges (super admin or admin)
   * @returns {boolean}
   */
  const isAdmin = useCallback(
    () => isAnyRole([ROLE_TYPES.SUPER_ADMIN, ROLE_TYPES.ADMIN]),
    [isAnyRole]
  )

  /**
   * Check if user is super admin
   * @returns {boolean}
   */
  const isSuperAdmin = useCallback(
    () => isRole(ROLE_TYPES.SUPER_ADMIN),
    [isRole]
  )

  /**
   * Check if user can create leads
   * @returns {boolean}
   */
  const canCreateLead = useCallback(
    () =>
      isAnyRole([
        ROLE_TYPES.SUPER_ADMIN,
        ROLE_TYPES.ADMIN,
        ROLE_TYPES.GENERAL_MANAGER,
        ROLE_TYPES.MANAGING_DIRECTOR,
        ROLE_TYPES.BRANCH_MANAGER,
        ROLE_TYPES.ENQUIRY_MANAGER,
        ROLE_TYPES.SALES_EXECUTIVE,
        ROLE_TYPES.PURCHASE_MANAGER,
      ]),
    [isAnyRole]
  )

  /**
   * Check if user can update leads
   * @returns {boolean}
   */
  const canUpdateLead = useCallback(
    () =>
      isAnyRole([
        ROLE_TYPES.SUPER_ADMIN,
        ROLE_TYPES.ADMIN,
        ROLE_TYPES.GENERAL_MANAGER,
        ROLE_TYPES.MANAGING_DIRECTOR,
        ROLE_TYPES.BRANCH_MANAGER,
        ROLE_TYPES.ENQUIRY_MANAGER,
        ROLE_TYPES.SALES_EXECUTIVE,
        ROLE_TYPES.PURCHASE_MANAGER,
        ROLE_TYPES.ENQUIRY_ANALYST
      ]),
    [isAnyRole]
  )

  /**
   * Check if user has management/deletion privileges
   * Includes: Super Admin, Admin, General Manager, Managing Director
   * Use case: Delete buttons, bulk operations
   * @returns {boolean}
   */
  const canManageDelete = useCallback(
    () =>
      isAnyRole([
        ROLE_TYPES.SUPER_ADMIN,
        ROLE_TYPES.ADMIN,
        ROLE_TYPES.GENERAL_MANAGER,
        ROLE_TYPES.MANAGING_DIRECTOR,
      ]),
    [isAnyRole]
  )

  /**
   * Check if user has extended management privileges including purchase manager
   * Includes: Super Admin, Admin, General Manager, Managing Director, Purchase Manager
   * Use case: Vehicle creation, extended admin operations
   * @returns {boolean}
   */
  const canManageExtended = useCallback(
    () =>
      isAnyRole([
        ROLE_TYPES.SUPER_ADMIN,
        ROLE_TYPES.ADMIN,
        ROLE_TYPES.GENERAL_MANAGER,
        ROLE_TYPES.MANAGING_DIRECTOR,
        ROLE_TYPES.PURCHASE_MANAGER,
      ]),
    [isAnyRole]
  )

  /**
   * Check if user is in the sales team
   * Includes: Enquiry Manager, Enquiry Analyst, Sales Manager, Sales Executive
   * Use case: Sales operations, lead management visibility
   * @returns {boolean}
   */
  const isSalesTeam = useCallback(
    () =>
      isAnyRole([
        ROLE_TYPES.ENQUIRY_MANAGER,
        ROLE_TYPES.ENQUIRY_ANALYST,
        ROLE_TYPES.SALES_MANAGER,
        ROLE_TYPES.SALES_EXECUTIVE,
      ]),
    [isAnyRole]
  )

  /**
   * Check if user can approve/authorize operations
   * Includes: Super Admin, Admin, General Manager, Managing Director, Enquiry Manager
   * Use case: Approval workflows, critical operations
   * @returns {boolean}
   */
  const canApprove = useCallback(
    () =>
      isAnyRole([
        ROLE_TYPES.SUPER_ADMIN,
        ROLE_TYPES.ADMIN,
        ROLE_TYPES.GENERAL_MANAGER,
        ROLE_TYPES.MANAGING_DIRECTOR,
        ROLE_TYPES.ENQUIRY_MANAGER,
      ]),
    [isAnyRole]
  )

  return {
    // Current role
    currentRole,

    // Role checks
    isRole,
    isAnyRole,
    isAdmin,
    isSuperAdmin,
    hasRoleOrHigher,
    hasRoleLower,

    // Role utilities
    getSubordinateRoles,
    getRoleHierarchy,
    getRoleDisplayLabel,

    // Permission checks
    canCreateLead,
    canUpdateLead,

    // Semantic role groups
    canManageDelete,
    canManageExtended,
    isSalesTeam,
    canApprove,


  }
}

export default useRole
